import { useCallback, useState } from 'react';
import type { AuthScope, AuthResponse, OAuthProvider, SocialAuthIntent } from '@/types/auth';
import type { AuthError } from '@/types/auth';
import { authService } from '@/services/auth.service';
import { socialAuthPlugin } from './plugin';

interface UseSocialAuthOptions {
  scope: AuthScope;
  onSuccess?: (payload: AuthResponse['data'], response: AuthResponse) => Promise<void> | void;
  onError?: (message: string) => void;
  tenantSlug?: string;
  loginHint?: string;
  prompt?: string;
}

interface UseSocialAuthResult {
  startLogin: (provider: OAuthProvider, intent?: SocialAuthIntent) => Promise<void>;
  isLoading: boolean;
  activeProvider: OAuthProvider | null;
  error: string | null;
}

function extractErrorMessage(error: unknown): string {
  if (!error) {
    return 'Unexpected authentication error.';
  }

  if (
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object'
  ) {
    const response = error.response as { data?: AuthError; message?: string };
    const dataMessage = response.data?.message ?? response.data?.error;
    if (typeof dataMessage === 'string' && dataMessage.length > 0) {
      return dataMessage;
    }
    if (typeof response.message === 'string' && response.message.length > 0) {
      return response.message;
    }
  }

  if (error instanceof Error && typeof error.message === 'string') {
    return error.message;
  }

  return 'Unable to continue with social sign-in. Please try again.';
}

export function useSocialAuth(options: UseSocialAuthOptions): UseSocialAuthResult {
  const [activeProvider, setActiveProvider] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startLogin = useCallback(
    async (provider: OAuthProvider, intent?: SocialAuthIntent) => {
      setError(null);
      setActiveProvider(provider);

      try {
        if (authService.isUsingMockData()) {
          const payload = await authService.performMockOAuth(provider, options.scope, {
            login_hint: options.loginHint,
            tenant_slug: options.tenantSlug,
            tenantSlug: options.tenantSlug,
            desired_role: intent?.desiredRole,
            desiredRole: intent?.desiredRole,
            team_name: intent?.teamName,
            teamName: intent?.teamName,
            team_seats:
              typeof intent?.teamSeats === 'number' ? String(intent.teamSeats) : intent?.teamSeats,
            teamSeats:
              typeof intent?.teamSeats === 'number' ? String(intent.teamSeats) : intent?.teamSeats,
            team_max_seats:
              typeof intent?.teamSeats === 'number' ? String(intent.teamSeats) : intent?.teamSeats,
            team_description: intent?.teamDescription,
            teamDescription: intent?.teamDescription,
          });

          if (!payload?.data) {
            throw new Error('Missing authentication payload from mock OAuth response.');
          }

          await options.onSuccess?.(payload.data, payload);
          setActiveProvider(null);
          return;
        }

        socialAuthPlugin.clearPendingState();
        await socialAuthPlugin.startRedirect({
          scope: options.scope,
          provider,
          tenantSlug: options.tenantSlug,
          loginHint: options.loginHint,
          prompt: options.prompt,
          desiredRole: intent?.desiredRole,
          teamName: intent?.teamName,
          teamSeats: intent?.teamSeats,
          teamDescription: intent?.teamDescription,
        });
      } catch (err) {
        const message = extractErrorMessage(err);
        setError(message);
        setActiveProvider(null);
        options.onError?.(message);
      }
    },
    [options]
  );

  return {
    startLogin,
    isLoading: activeProvider !== null,
    activeProvider,
    error,
  };
}
